import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShareIcon from '@mui/icons-material/Share';
import { Alert, Box, IconButton, Paper, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useExportWallImage } from '../../board/export/useExportWallImage';
import { useExportWallPdf } from '../../board/export/useExportWallPdf';
import { wallsApi, wallStorage } from '@walls/services/wallsApi';
import {
  useWallContentStore,
  useWallAiJobStore,
  useWallParticipantsStore,
  useWallStore,
  useWorkspaceUiStore,
  type WallAiJob,
  type WallBoard,
} from '@walls/store';
import { AiActionMenu } from '../ai/AiActionMenu';
import { GroupByGroupDialog } from '../ai/GroupByGroupDialog';
import { GroupByThemeDialog } from '../ai/GroupByThemeDialog';
import { OrderPageCardsDialog } from '../ai/OrderPageCardsDialog';
import { useGroupCardsByGroup } from '../ai/hooks/useGroupCardsByGroup';
import { useGroupCardsByTheme } from '../ai/hooks/useGroupCardsByTheme';
import { useOrderPageCards } from '../ai/hooks/useOrderPageCards';
import { getThemeGroupingEligibleCards, maxThemeGroupingCards } from '../ai/themeGrouping';
import { getPanelColor, getPanelTitleColor } from '../theme/wallThemeStyles';
import { BoardActionMenu } from './BoardActionMenu';
import { SaveWallTemplateDialog } from './SaveWallTemplateDialog';
import { WallHandIcon } from './WallHandIcon';

interface WorkspaceActionsProps {
  board: WallBoard;
  showHandTool: boolean;
}

export function WorkspaceActions({ board, showHandTool }: WorkspaceActionsProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [aiMenuAnchor, setAiMenuAnchor] = useState<HTMLElement | null>(null);
  const [groupByGroupDialogOpen, setGroupByGroupDialogOpen] = useState(false);
  const [groupByThemeDialogOpen, setGroupByThemeDialogOpen] = useState(false);
  const [groupByThemeError, setGroupByThemeError] = useState<string | null>(null);
  const [isGroupingByTheme, setIsGroupingByTheme] = useState(false);
  const [orderPageCardsDialogOpen, setOrderPageCardsDialogOpen] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateSavedOpen, setTemplateSavedOpen] = useState(false);
  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const groups = useWallParticipantsStore((state) => state.groups);
  const cards = useWallContentStore((state) => state.cards);
  const themeGroupingJob = useWallAiJobStore((state) => state.themeGroupingJob);
  const setThemeGroupingJob = useWallAiJobStore((state) => state.setThemeGroupingJob);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const setActiveOverlay = useWorkspaceUiStore((state) => state.setActiveOverlay);
  const theme = useWallStore((state) => state.theme);
  const toggleParticipantPanel = useWorkspaceUiStore((state) => state.toggleParticipantPanel);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const zoomIn = useWorkspaceUiStore((state) => state.zoomIn);
  const zoomOut = useWorkspaceUiStore((state) => state.zoomOut);
  const groupCardsByGroup = useGroupCardsByGroup(board);
  const groupCardsByTheme = useGroupCardsByTheme(board);
  const orderPageCards = useOrderPageCards(board);
  const { exportWallImage, isExporting: isExportingImage } = useExportWallImage(board);
  const { exportWallPdf, isExporting: isExportingPdf } = useExportWallPdf(board);
  const exportInProgress = isExportingImage || isExportingPdf;
  const panelColor = getPanelColor(theme);
  const panelTitleColor = getPanelTitleColor(theme);

  const canShare = !board.isPrivate && Boolean(board.isOwner);
  const canUseAiTools = Boolean(board.isOwner && board.allowAiTools);
  const canUseAiGrouping = canUseAiTools && board.canUseAiGrouping === true;
  const canSaveTemplates = board.canSaveTemplates === true;
  const groupByGroupDisabled = groups.length === 0;
  const themeGroupingCardCount = Math.min(
    getThemeGroupingEligibleCards(cards).length,
    maxThemeGroupingCards
  );
  const themeGroupingActive =
    themeGroupingJob?.status === 'queued' ||
    themeGroupingJob?.status === 'processing' ||
    themeGroupingJob?.status === 'applying';
  const groupByThemeDisabled =
    !canUseAiGrouping || themeGroupingCardCount < 2 || themeGroupingActive;

  useEffect(() => {
    if (canUseAiTools) {
      return;
    }

    setAiMenuAnchor(null);
    setGroupByGroupDialogOpen(false);
    setGroupByThemeDialogOpen(false);
    setOrderPageCardsDialogOpen(false);
  }, [canUseAiTools]);

  useEffect(() => {
    if (!canUseAiGrouping) {
      setThemeGroupingJob(null);
      return;
    }

    void wallsApi
      .getThemeGroupingJob(board.id, useWallStore.getState().topicKey)
      .then((job) => {
        if (job || !useWallAiJobStore.getState().themeGroupingJob) {
          setThemeGroupingJob(job);
        }
      })
      .catch(() => undefined);
  }, [board.id, canUseAiGrouping, setThemeGroupingJob]);

  useEffect(() => {
    if (!canUseAiGrouping || !themeGroupingActive) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      void wallsApi
        .getThemeGroupingJob(board.id, useWallStore.getState().topicKey)
        .then((job) => {
          if (job) {
            setThemeGroupingJob(job);
          }
        })
        .catch(() => undefined);
    }, 3000);

    return () => window.clearInterval(interval);
  }, [board.id, canUseAiGrouping, setThemeGroupingJob, themeGroupingActive]);

  const closeMenu = () => setMenuAnchor(null);
  const closeAiMenu = () => setAiMenuAnchor(null);

  const openAiMenu = (anchorEl: HTMLElement) => {
    if (!canUseAiTools) {
      return;
    }

    closeMenu();
    setAiMenuAnchor(anchorEl);
  };

  const openMenu = (anchorEl: HTMLElement) => {
    closeAiMenu();
    setMenuAnchor(anchorEl);
  };

  const orderCards = () => {
    if (!canUseAiTools) {
      return;
    }

    closeAiMenu();
    setOrderPageCardsDialogOpen(true);
  };

  const groupByGroup = () => {
    if (!canUseAiTools) {
      return;
    }

    closeAiMenu();
    setGroupByGroupDialogOpen(true);
  };

  const groupByTheme = () => {
    if (!canUseAiGrouping || groupByThemeDisabled) {
      return;
    }

    closeAiMenu();
    setGroupByThemeError(null);
    setGroupByThemeDialogOpen(true);
  };

  const confirmOrderCards = () => {
    if (!canUseAiTools) {
      return;
    }

    setOrderPageCardsDialogOpen(false);
    orderPageCards();
  };

  const confirmGroupByGroup = () => {
    if (!canUseAiTools) {
      return;
    }

    setGroupByGroupDialogOpen(false);
    void groupCardsByGroup();
  };

  const confirmGroupByTheme = async () => {
    if (!canUseAiGrouping || isGroupingByTheme) {
      return;
    }

    setIsGroupingByTheme(true);
    setGroupByThemeError(null);

    try {
      await groupCardsByTheme();
      setGroupByThemeDialogOpen(false);
    } catch (error) {
      setGroupByThemeError(
        error instanceof Error ? error.message : 'Unable to group cards by theme.'
      );
    } finally {
      setIsGroupingByTheme(false);
    }
  };

  const logOut = () => {
    wallStorage.clearAuth();
    window.location.reload();
  };

  const openFeedback = () => {
    closeMenu();
    setActiveOverlay('feedback');
  };

  const openTheme = () => {
    closeMenu();
    setActiveOverlay('theme');
  };

  const openSaveTemplate = () => {
    if (!canSaveTemplates) {
      return;
    }

    setSaveTemplateError(null);
    setSaveTemplateDialogOpen(true);
  };

  const saveTemplate = async (input: { name: string; description: string }) => {
    setIsSavingTemplate(true);
    setSaveTemplateError(null);

    try {
      await wallsApi.saveWallTemplate(board.id, input);
      setSaveTemplateDialogOpen(false);
      setTemplateSavedOpen(true);
    } catch (error) {
      setSaveTemplateError(error instanceof Error ? error.message : 'Unable to save template.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const exportPdf = () => {
    exportWallPdf().catch((error) => {
      console.error('PDF export failed', error);
      setExportErrorOpen(true);
    });
  };

  const exportImage = () => {
    exportWallImage().catch((error) => {
      console.error('Image export failed', error);
      setExportErrorOpen(true);
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        bgcolor: panelColor,
        boxShadow: '0px 3px 6px #00000029',
        height: 50,
        position: 'fixed',
        right: { xs: 10, sm: 20 },
        top: { xs: 10, sm: 20 },
        zIndex: 2000,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
        {canShare && (
          <Tooltip title="Share">
            <IconButton
              onClick={() => setActiveOverlay('share')}
              aria-label="Share"
              sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
        )}
        {canUseAiTools && (
          <Tooltip title="AI">
            <IconButton
              onClick={(event) => openAiMenu(event.currentTarget)}
              aria-label="AI tools"
              sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
            >
              <AutoAwesomeIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Participants">
          <IconButton
            onClick={toggleParticipantPanel}
            aria-label="Participants"
            sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
          >
            <Box
              component="img"
              sx={{ height: '16px' }}
              src="https://storage.googleapis.com/dotstorming-img/participants.svg"
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Menu">
          <IconButton
            onClick={(event) => openMenu(event.currentTarget)}
            aria-label="Menu"
            sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
          >
            <Box
              component="img"
              sx={{ height: '16px' }}
              src="https://storage.googleapis.com/dotstorming-img/wall-menu.svg"
            />
          </IconButton>
        </Tooltip>
      </Stack>
      <AiActionMenu
        anchorEl={aiMenuAnchor}
        groupByGroupDisabled={groupByGroupDisabled}
        groupByThemeDisabled={groupByThemeDisabled}
        groupByThemeSubscriptionRequired={!canUseAiGrouping}
        onClose={closeAiMenu}
        onGroupByGroup={groupByGroup}
        onGroupByTheme={groupByTheme}
        onOrderCards={orderCards}
      />
      <GroupByGroupDialog
        open={groupByGroupDialogOpen}
        onClose={() => setGroupByGroupDialogOpen(false)}
        onConfirm={confirmGroupByGroup}
      />
      <GroupByThemeDialog
        cardCount={themeGroupingCardCount}
        error={groupByThemeError}
        isGrouping={isGroupingByTheme}
        open={groupByThemeDialogOpen}
        onClose={() => setGroupByThemeDialogOpen(false)}
        onConfirm={() => void confirmGroupByTheme()}
      />
      <OrderPageCardsDialog
        open={orderPageCardsDialogOpen}
        onClose={() => setOrderPageCardsDialogOpen(false)}
        onConfirm={confirmOrderCards}
      />
      {showHandTool && (
        <Stack
          direction="row"
          spacing={1}
          data-testid="board-navigation-controls"
          sx={{
            alignItems: 'center',
            bottom: { xs: 10, sm: 20 },
            position: 'fixed',
            right: { xs: 10, sm: 20 },
            zIndex: 1000,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              alignItems: 'center',
              bgcolor: panelColor,
              borderRadius: '8px',
              boxShadow: '0px 3px 6px #00000029',
              display: 'flex',
              height: 50,
              justifyContent: 'center',
              width: 50,
            }}
          >
            <IconButton
              color={selectedTool === 'hand' ? 'primary' : 'default'}
              onClick={() => setSelectedTool(selectedTool === 'hand' ? 'pointer' : 'hand')}
              aria-label="Hand"
              sx={{
                color: selectedTool === 'hand' ? 'primary.main' : panelTitleColor,
                p: 0,
                mt: '5px',
              }}
            >
              <WallHandIcon />
            </IconButton>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '8px',
              bgcolor: panelColor,
              boxShadow: '0px 3px 6px #00000029',
              height: 50,
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
              <IconButton
                onClick={zoomOut}
                aria-label="Zoom out"
                sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
              >
                -
              </IconButton>
              <Typography
                variant="body2"
                sx={{ color: panelTitleColor, minWidth: 54, textAlign: 'center' }}
              >
                {Math.floor(zoom * 100)}%
              </Typography>
              <IconButton
                onClick={zoomIn}
                aria-label="Zoom in"
                sx={{ borderRadius: 0, color: panelTitleColor, height: '100%', px: 2 }}
              >
                +
              </IconButton>
            </Stack>
          </Paper>
        </Stack>
      )}
      <BoardActionMenu
        anchorEl={menuAnchor}
        board={board}
        exportInProgress={exportInProgress}
        imageExporting={isExportingImage}
        onClose={closeMenu}
        canSaveTemplates={canSaveTemplates}
        onExportImage={exportImage}
        onExportPdf={exportPdf}
        onEditTheme={openTheme}
        onFeedback={openFeedback}
        onSaveTemplate={openSaveTemplate}
        pdfExporting={isExportingPdf}
        onLogOut={logOut}
      />
      <SaveWallTemplateDialog
        open={saveTemplateDialogOpen}
        error={saveTemplateError}
        isSaving={isSavingTemplate}
        onClose={() => setSaveTemplateDialogOpen(false)}
        onSave={(input) => void saveTemplate(input)}
      />
      <Snackbar
        open={templateSavedOpen}
        autoHideDuration={4000}
        onClose={() => setTemplateSavedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setTemplateSavedOpen(false)}>
          Template saved.
        </Alert>
      </Snackbar>
      <Snackbar
        open={exportErrorOpen}
        autoHideDuration={5000}
        onClose={() => setExportErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setExportErrorOpen(false)}>
          Export failed. Please try again.
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(themeGroupingJob)}
        autoHideDuration={
          themeGroupingJob?.status === 'completed' || themeGroupingJob?.status === 'failed'
            ? 5000
            : null
        }
        onClose={() => {
          if (
            themeGroupingJob?.status === 'completed' ||
            themeGroupingJob?.status === 'failed'
          ) {
            setThemeGroupingJob(null);
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={themeGroupingJob?.status === 'failed' ? 'error' : 'info'}
          variant="filled"
          onClose={
            themeGroupingJob?.status === 'completed' || themeGroupingJob?.status === 'failed'
              ? () => setThemeGroupingJob(null)
              : undefined
          }
        >
          {getThemeGroupingStatusMessage(themeGroupingJob)}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

function getThemeGroupingStatusMessage(job: WallAiJob | null) {
  switch (job?.status) {
    case 'completed':
      return 'Cards grouped by theme.';
    case 'failed':
      return job.error || 'Unable to group cards by theme.';
    case 'applying':
      return 'Applying theme groups to the wall…';
    case 'processing':
      return 'AI is grouping cards by theme…';
    default:
      return 'Theme grouping queued…';
  }
}
